import moment from "moment";

import { TransferableAddress, TransferableEmail, TransferableHealthIdentifier, TransferablePatientInfoResponse, TransferablePhone } from "@/Common/Api/ExternalCommunications/Dto/TransferablePatientInfoResponse";
import { DateTime } from "@/Common/Helpers/DateTime";
import { PatientHelper } from "@/Common/Helpers/PatientHelper";
import { IImportablePatient, IPatient } from "@/Common/Models/Patient/IPatient";
import { IPatientCreationModel } from "@/Common/Models/Patient/UI/IPatientCreationModel";
import { ExternalPatientIdentifierResponse } from "./ExternalPatientIdentifierResponse";

class ExternalPatientResponse implements IPatient, IImportablePatient {
    public Id: string;
    public FirstName: string;
    public PreferredName: string;
    public LastName: string;
    public DateOfBirth: Date;
    public ClientTag: string;
    public TransferablePatientInfo: TransferablePatientInfoResponse;
    public PatientIdentifiers: ExternalPatientIdentifierResponse[];

    public get PatientId(): string {
        return this.Id;
    }

    public get NAM(): string {
        if (isArrayNullOrUndefinedOrEmpty(this.PatientIdentifiers))
            return null;

        return this.PatientIdentifiers[0].Value;
    }

    public isInternal(): boolean {
        return false;
    }

    public toTransferable(): TransferablePatientInfoResponse {
        return this.TransferablePatientInfo;
    }

    public toPatientCreationModel(): IPatientCreationModel {
        let healthCard: TransferableHealthIdentifier = null;
        if (!isArrayNullOrUndefinedOrEmpty(this.TransferablePatientInfo.HealthIdentifiers))
            healthCard = this.TransferablePatientInfo.HealthIdentifiers.sortByPropertyDesc(x => x.ExpiryDate || DateTime.MinValue).firstOrDefault();

        let addresses = isArrayNullOrUndefinedOrEmpty(this.TransferablePatientInfo.Addresses)
            ? []
            : this.TransferablePatientInfo.Addresses.map(x => TransferableAddress.toCommonAddress(x));

        let emails = isArrayNullOrUndefinedOrEmpty(this.TransferablePatientInfo.Emails)
            ? []
            : this.TransferablePatientInfo.Emails
                .sortByPropertyDesc(x => x.IsOkToContact)
                .map(x => TransferableEmail.toCommonEmail(x));

        let phone1 = this.TransferablePatientInfo.Phones?.firstPreferredOrDefault(x => x.IsPreferred) ?? new TransferablePhone();

        let phone2 = this.TransferablePatientInfo.Phones?.length > 1
            ? this.TransferablePatientInfo.Phones?.firstOrDefault(x => x.PhoneNumber != phone1.PhoneNumber, new TransferablePhone())
            : new TransferablePhone();

        let newPatientModel = PatientHelper.getDefaultPatientCreationModel();
        newPatientModel.FirstName = this.TransferablePatientInfo.FirstName;
        newPatientModel.LastName = this.TransferablePatientInfo.LastName;
        newPatientModel.GenderLookupId = this.TransferablePatientInfo.Gender?.Id;
        newPatientModel.DOB = this.TransferablePatientInfo.DateOfBirth;
        newPatientModel.SelectedPrefixLookupId = this.TransferablePatientInfo.Prefix?.Id;
        newPatientModel.selectedHealthCardRegionLookupId = healthCard?.Province?.Id ?? cacheStore.GetDefaultRegionForActiveSite().LookupID;
        newPatientModel.NAMEdit = healthCard?.Value;
        newPatientModel.NAMExpiryEdit = healthCard?.ExpiryDate && moment(healthCard.ExpiryDate).startOf("month").toDate();
        newPatientModel.motherFirstName = this.TransferablePatientInfo.MotherFirstName;
        newPatientModel.motherLastName = this.TransferablePatientInfo.MotherLastName;
        newPatientModel.PreferredSpokenLanguageLookupId = this.TransferablePatientInfo.PreferredSpokenLanguage?.Id;

        newPatientModel.Addresses = addresses;

        let address = addresses.firstPreferredOrDefault(x => x.IsPreferred);
        if (address) {
            newPatientModel.CivicAddressEdit = address.StreetAddress;
            newPatientModel.SelectedCountryLookupId = address.CountryLookupId;
            newPatientModel.SelectedRegionLookupId = address.StateLookupId;
        }
        else if (healthCard?.Province) {
            newPatientModel.SelectedRegionLookupId = healthCard.Province.Id;
            newPatientModel.SelectedCountryLookupId = cacheStore.GetCountryForRegion(newPatientModel.SelectedRegionLookupId)?.LookupID;
        }

        newPatientModel.Phone1Edit = phone1.PhoneNumber;
        newPatientModel.Phone1TypeLookupId = phone1.Type?.Id;
        newPatientModel.Phone1ContactTypeLookupId = phone1.ContactType?.Id;
        newPatientModel.Phone1Extension = phone1.Extension;

        newPatientModel.Phone2Edit = phone2.PhoneNumber;
        newPatientModel.Phone2TypeLookupId = phone2.Type?.Id;
        newPatientModel.Phone2ContactTypeLookupId = phone2.ContactType?.Id;
        newPatientModel.Phone2Extension = phone2.Extension;

        newPatientModel.Emails = emails;
        newPatientModel.EmailEdit = emails.firstOrDefault()?.Address;
        newPatientModel.IsOkToContact = emails.firstOrDefault()?.IsOkToContact;
        newPatientModel.EmailTypeByLookup = emails.firstOrDefault()?.ContactPointTypeLookupId;

        return Immutable.fromJS(newPatientModel);
    }

    public static fromResponse(emp: Partial<ExternalPatientResponse>): ExternalPatientResponse {
        if (!emp)
            return null;

        return (Object as any).assign(new ExternalPatientResponse(), emp);
    }
}

export { ExternalPatientResponse };
